import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSigninIdentities } from "../lib/queries";
import { ProviderMark } from "../components/icons";
import { Alert, Button, Card, CardHeader, Field, Input } from "../components/ui";
import { API_URL, ApiError, api } from "../lib/api";
import { relativeTime } from "../lib/format";
import { TwoFactorSection } from "./security/TwoFactorSection";

/**
 * Your account's own credentials, and nothing else.
 *
 * API keys used to be the second of two tabs here, and the split was never quite
 * right: this page reaches the user menu because a password and a second factor
 * belong to the person signed in, while an API key belongs to the organization —
 * it carries a role over that organization's apps and it spends its credit.
 *
 * With one section left, the tab bar went with the move. Two tabs where one is
 * always empty of choices is navigation for its own sake, and it cost this page a
 * heading that had to describe both audiences at once.
 */
export function SecurityPage() {
  const { t } = useTranslation("settings");
  return (
    <div className="space-y-6">
      {/* Kept as a lead paragraph rather than a second page header. The shell
          above already says whose account this is; what this adds is what
          *this* section of it covers. */}
      <p className="text-[13px] leading-6 text-fg-muted">{t("security.lead")}</p>

      <TwoFactorSection />

      <LinkedAccountsSection />

      <p className="text-xs text-fg-subtle">
        <Trans
          ns="settings"
          i18nKey="security.apiKeys"
          components={{
            keys: <Link to="/settings/api-keys" className="text-accent underline underline-offset-2" />,
          }}
        />
      </p>
    </div>
  );
}

/**
 * The GitHub or GitLab accounts that can sign in as this person, and the
 * way to take one off. Unlinking asks for the password on purpose: an
 * account that signed up socially has none until "Forgot password" sets
 * one, and the API says so rather than letting it unlink its only way in.
 */
function LinkedAccountsSection() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const client = useQueryClient();
  const identities = useSigninIdentities();
  const providers = useQuery({
    queryKey: ["oauth-providers"],
    queryFn: () => api<{ data: { id: string; label: string }[] }>("/v1/auth/oauth/providers", { anonymous: true, omitCredentials: true }),
    staleTime: 3_600_000,
  });
  const [params, setParams] = useSearchParams();
  // What the callback said on the way back: linked, or why not. Read once
  // and cleared from the URL, so a reload does not repeat it.
  const linkedNotice = params.get("linked");
  const linkError = params.get("link_error");
  const dismiss = () => {
    const next = new URLSearchParams(params);
    next.delete("linked");
    next.delete("link_error");
    setParams(next, { replace: true });
  };
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const unlink = useMutation({
    mutationFn: (input: { provider: string; password: string }) =>
      api<void>(`/v1/auth/oauth/identities/${input.provider}`, { method: "DELETE", body: { password: input.password } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["oauth-identities"] });
      setUnlinking(null);
      setPassword("");
      setError(null);
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t("linked.unlinkFailed")),
  });

  const available = providers.data?.data ?? [];
  const linked = identities.data ?? [];
  // Nothing configured and nothing linked: the section has nothing to say.
  // Staff never see it: they sign in with a password and authenticator code,
  // and the API refuses them a link anyway.
  if ((available.length === 0 && linked.length === 0) || user?.platformRole) return null;
  const label = (id: string) => available.find((p) => p.id === id)?.label ?? id;

  return (
    <Card>
      <CardHeader title={t("linked.title")} description={t("linked.description")} />
      {(linkedNotice || linkError) && (
        <div className="px-4 pt-4">
          <Alert tone={linkError ? "error" : "success"}>
            <span className="flex items-start justify-between gap-3">
              <span>
                {linkError
                  ? linkError === "failed"
                    ? t("linked.linkFailed")
                    : linkError
                  : t("linked.linkedNow", { provider: label(linkedNotice ?? "") })}
              </span>
              <button type="button" onClick={dismiss} className="text-xs underline underline-offset-2">
                {t("linked.dismiss")}
              </button>
            </span>
          </Alert>
        </div>
      )}
      <ul className="divide-y divide-line">
        {available.map((provider) => {
          const identity = linked.find((i) => i.provider === provider.id);
          return (
            <li key={provider.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
              <ProviderMark provider={provider.id} className={`size-5 ${provider.id === "gitlab" ? "text-[#fc6d26]" : "text-fg"}`} />
              <span className="font-medium text-fg">{provider.label}</span>
              {identity ? (
                <>
                  <span className="text-fg-subtle">
                    {t("linked.as", { login: identity.login })} · {t("linked.lastUsed", { when: relativeTime(identity.lastUsedAt) })}
                  </span>
                  <Button variant="ghost" size="sm" className="ml-auto" onClick={() => { setUnlinking(provider.id); setError(null); }}>
                    {t("linked.unlink")}
                  </Button>
                </>
              ) : (
                <a
                  href={`${API_URL}/v1/auth/oauth/${provider.id}/start?link=1&return_to=${encodeURIComponent("/settings/security")}`}
                  className="ml-auto text-sm text-accent hover:underline"
                >
                  {t("linked.link")}
                </a>
              )}
            </li>
          );
        })}
      </ul>
      {unlinking && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            unlink.mutate({ provider: unlinking, password });
          }}
          className="space-y-3 border-t border-line px-4 py-4"
        >
          {error && <Alert>{error}</Alert>}
          <Field label={t("linked.password")} htmlFor="unlink-password" hint={t("linked.passwordHint", { provider: label(unlinking) })}>
            <Input id="unlink-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setUnlinking(null)} disabled={unlink.isPending}>
              {t("linked.cancel")}
            </Button>
            <Button type="submit" variant="danger" loading={unlink.isPending} disabled={password.length === 0}>
              {t("linked.confirm", { provider: label(unlinking) })}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
